/**
 * The one place 21 Wild asks for notification permission.
 *
 * Asked once, in the only moment where the answer is obviously worth
 * something: just after creating a challenge, when the player is about to wait
 * for somebody they cannot see to take it. A permission prompt on load is a
 * prompt with no context, and it is answered "no" and never asked again —
 * browsers only give you the one chance.
 */

import { el } from '@/ui/hud';
import { enablePush, pushState, type PushState } from '@/net/notify';

/** How long a dismissal lasts. */
const SNOOZE_DAYS = 7;
const SNOOZE_KEY = 'w21.push.snoozed';

function snoozed(): boolean {
  try {
    const until = Number(localStorage.getItem(SNOOZE_KEY) ?? '0');
    return Number.isFinite(until) && until > Date.now();
  } catch {
    // Private mode, or storage the browser has decided to refuse. A card shown
    // once too often is a smaller failure than one that never appears.
    return false;
  }
}

function snooze(): void {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 86_400_000));
  } catch {
    // Nothing to do; the card will offer itself again next time.
  }
}

/**
 * The card on the creator's challenge page.
 *
 * Hidden unless it has something to offer: push already on, permission already
 * denied, the deployment running without VAPID keys, or a recent dismissal all
 * mean there is nothing to ask and the card stays out of the way.
 */
export class PushPrompt {
  readonly root: HTMLElement;

  readonly #long = el('span', 'w21-pp-long', 'Get told when someone takes it');
  readonly #short = el('span', 'w21-pp-short', 'Get told when it’s taken');
  readonly #body = el('span', 'w21-pp-body');
  readonly #action: HTMLButtonElement;
  #state: PushState = 'unsupported';
  /** Pinned by the frame harness: a later `offer()` must not undo it. */
  #forced = false;

  constructor() {
    // The kit's `PushPrompt`: close · icon · title · body · button, on a grid
    // that puts the icon beside the title on a desk and the body and button
    // under both on a phone (≤ 600). The title has a long and a short form
    // and the stylesheet picks one, so the box never grows a second line for
    // a word that would not fit.
    this.root = el('div', 'w21-push-prompt');
    this.root.hidden = true;
    this.root.setAttribute('role', 'region');
    this.root.setAttribute('aria-label', 'Notifications');

    const close = el('button', 'w21-pp-close w21-icon-btn w21-sm') as HTMLButtonElement;
    close.type = 'button';
    close.setAttribute('aria-label', 'Not now');
    close.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>';
    close.addEventListener('click', (event) => {
      event.stopPropagation();
      snooze();
      this.root.hidden = true;
    });

    const icon = el('span', 'w21-pp-icon');
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z"></path><path d="M10 21a2 2 0 0 0 4 0"></path></svg>';

    const title = el('b', 'w21-pp-title');
    title.append(this.#long, this.#short);

    this.#action = el('button', 'w21-btn w21-primary w21-sm') as HTMLButtonElement;
    this.#action.type = 'button';
    this.#action.textContent = 'Turn on notifications';
    this.#action.addEventListener('click', () => void this.#turnOn());

    this.root.append(close, icon, title, this.#body, this.#action);
  }

  /** Decide whether to offer anything. Call when the creator's page opens. */
  async offer(isCreator: boolean): Promise<void> {
    // One gate, checked before anything else. The card was appearing on won,
    // lost, pending and in-play challenges with nothing in its body, because
    // the caller's condition and the card's own state were two separate
    // opinions about whether there was an offer to make.
    if (!isCreator || snoozed()) {
      this.root.hidden = true;
      return;
    }
    if (this.#forced) return;
    const state = await pushState();
    // `force()` may have landed while the browser was asked; the harness wins.
    if (this.#forced) return;
    this.#state = state;
    this.#paint();
  }

  /**
   * Force a variant, for the frame harness only.
   *
   * This card's states depend on the browser's permission model and on the
   * device — `needs-install` is literally "iOS Safari, not on the Home
   * Screen", which a desktop capture cannot produce at all. So the harness
   * sets the state and the card draws it exactly as it always would.
   */
  force(variant: string): void {
    this.#forced = true;
    this.#state = variant as PushState;
    this.#paint();
  }

  #paint(): void {
    // 'on' — already done. 'denied' — the browser will not ask again and a
    // button that cannot work is worse than no button. 'unsupported' — nothing
    // to turn on, including the case where this deployment has no VAPID keys.
    if (this.#state === 'on' || this.#state === 'denied' || this.#state === 'unsupported') {
      this.root.hidden = true;
      return;
    }

    this.root.hidden = false;
    this.root.dataset.variant = this.#state;

    if (this.#state === 'needs-install') {
      // iOS Safari grants no push permission to a site in the browser, only to
      // one on the Home Screen. A "turn on" button here opens a prompt that
      // never appears, so the card explains the actual step instead.
      this.#long.textContent = 'Add 21 Wild to your Home Screen';
      this.#short.textContent = 'Add to Home Screen';
      // The two things the player has to FIND in their own menus are marked
      // up rather than left in the sentence: they are labels to be matched
      // against a screen, and a label broken across two lines stops looking
      // like the thing they are hunting for.
      this.#body.replaceChildren(
        document.createTextNode('iPhone only sends notifications to apps on the Home Screen. Tap '),
        el('b', undefined, 'Share'),
        document.createTextNode(', then '),
        el('b', undefined, 'Add to Home Screen'),
        document.createTextNode(', and open 21 Wild from there.'),
      );
      this.#action.hidden = true;
      return;
    }

    this.#long.textContent = 'Get told when someone takes it';
    this.#short.textContent = 'Get told when it’s taken';
    this.#body.textContent = 'Even with 21 Wild closed — and when the match settles.';
    this.#action.hidden = false;
  }

  async #turnOn(): Promise<void> {
    this.#action.disabled = true;
    this.#action.textContent = 'Asking…';
    this.#state = await enablePush();
    this.#action.disabled = false;
    this.#action.textContent = 'Turn on notifications';
    this.#paint();
  }
}
